import React, { useState } from 'react';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Slider,
} from '@mui/material';
import { useCreateRole } from '../hooks/useRoles';
import { useOrganizations } from '../hooks/useFleet';
import type { RoleScope } from '../api/types';
import { DraggableDialog } from './DraggableDialog';

interface RoleCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const RoleCreateDialog: React.FC<RoleCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scope: 'organization' as RoleScope,
    organization_id: '',
    priority: 25,
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createMutation = useCreateRole();
  const { data: orgsData } = useOrganizations({ size: 100 });

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      scope: 'organization',
      organization_id: '',
      priority: 25,
    });
    setSubmitError(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    // Validation
    if (!formData.name.trim()) {
      setSubmitError('Name is required');
      return;
    }

    if (formData.scope === 'organization' && !formData.organization_id) {
      setSubmitError('Organization is required for organization-scoped roles');
      return;
    }

    try {
      await createMutation.mutateAsync({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        scope: formData.scope,
        organization_id: formData.scope === 'organization' ? formData.organization_id : undefined,
        priority: formData.priority,
      });

      onSuccess?.();
      handleClose();
    } catch (err: any) {
      console.error('Create role error:', err);
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to create role');
    }
  };

  const actionsContent = (
    <>
      <Button onClick={handleClose}>Cancel</Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={createMutation.isPending}
        sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
      >
        {createMutation.isPending ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          'Create Role'
        )}
      </Button>
    </>
  );

  return (
    <DraggableDialog
      open={open}
      onClose={handleClose}
      title="Create New Role"
      maxWidth="sm"
      actions={actionsContent}
    >
      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextField
          label="Role Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          fullWidth
          required
          placeholder="Content Manager"
          helperText="A descriptive name for this role"
        />

        <TextField
          label="Description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          fullWidth
          multiline
          rows={2}
          placeholder="Manages content and playlists"
          helperText="Optional description of what this role can do"
        />

        <FormControl fullWidth>
          <InputLabel>Scope</InputLabel>
          <Select
            value={formData.scope}
            label="Scope"
            onChange={(e) => setFormData({ ...formData, scope: e.target.value as RoleScope })}
          >
            <MenuItem value="organization">Organization - Applies to specific organization</MenuItem>
            <MenuItem value="resource">Resource - Applies to specific resources</MenuItem>
            <MenuItem value="global" disabled>Global - Platform-wide (admin only)</MenuItem>
          </Select>
        </FormControl>

        {formData.scope === 'organization' && (
          <FormControl fullWidth required>
            <InputLabel>Organization</InputLabel>
            <Select
              value={formData.organization_id}
              label="Organization"
              onChange={(e) => setFormData({ ...formData, organization_id: e.target.value })}
            >
              {(orgsData?.data || []).map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Priority: {formData.priority}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Higher priority roles take precedence when permissions conflict.
            System roles: Global Admin (1000), Org Admin (100), Editor (50), Viewer (10)
          </Typography>
          <Slider
            value={formData.priority}
            onChange={(_, value) => setFormData({ ...formData, priority: value as number })}
            min={1}
            max={99}
            marks={[
              { value: 10, label: '10' },
              { value: 25, label: '25' },
              { value: 50, label: '50' },
              { value: 75, label: '75' },
            ]}
            valueLabelDisplay="auto"
          />
        </Box>
      </Box>
    </DraggableDialog>
  );
};

export default RoleCreateDialog;
